import { httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import { functions, db } from '../firebase';
import type {
  ConsentCollectionSnapshot,
  ConsentEventInput,
  ConsentStatus,
  ConsentType,
} from '../types/consent';
import {
  CONCIERGE_CONSENT_VERSION,
  ONBOARDING_CONSENT_VERSION,
  REQUIRED_CONSENT_TYPES,
} from '../types/consent';

interface RecordConsentEventsInput {
  userId?: string;
  events: ConsentEventInput[];
}

interface RecordConsentEventsResponse {
  eventIds: string[];
}

const recordConsentEventsCallable = httpsCallable<RecordConsentEventsInput, RecordConsentEventsResponse>(
  functions,
  'recordConsentEvents',
);

export async function recordConsentEvents(input: RecordConsentEventsInput) {
  const response = await recordConsentEventsCallable(input);
  return response.data;
}

export function buildBookPageConsentEvents(snapshot: ConsentCollectionSnapshot): ConsentEventInput[] {
  return [
    { consentType: 'terms', version: ONBOARDING_CONSENT_VERSION, status: snapshot.terms ? 'granted' : 'declined', sourceSurface: 'book_page' },
    { consentType: 'privacy', version: ONBOARDING_CONSENT_VERSION, status: snapshot.privacy ? 'granted' : 'declined', sourceSurface: 'book_page' },
    {
      consentType: 'health_data_processing',
      version: ONBOARDING_CONSENT_VERSION,
      status: snapshot.healthDataProcessing ? 'granted' : 'declined',
      sourceSurface: 'book_page',
    },
    {
      consentType: 'genetics_context',
      version: ONBOARDING_CONSENT_VERSION,
      status: snapshot.geneticsContext ? 'granted' : 'declined',
      sourceSurface: 'book_page',
    },
    // §4.1(4). The one genuinely optional confirmation: a member who declines it
    // still gets the Service, and Melio does not keep their raw genotypes for
    // re-analysis. `research_use` and `commercial_data_use` used to sit here and
    // no longer do — §4.4 forbids seeking consent for a purpose §4.1 does not list.
    {
      consentType: 'genetic_data_retention',
      version: ONBOARDING_CONSENT_VERSION,
      status: snapshot.geneticDataRetention ? 'granted' : 'declined',
      sourceSurface: 'book_page',
    },
  ];
}

export function buildConciergeConsentEvents(input: {
  actOnBehalf: boolean;
  shareWithProvider: boolean;
  accessInsuranceInfo: boolean;
  caseId?: string | null;
  providerName?: string | null;
}): ConsentEventInput[] {
  const artifactRef = input.caseId
    ? {
        collection: 'cases',
        documentId: input.caseId,
        path: `cases/${input.caseId}`,
        note: input.providerName || null,
      }
    : undefined;

  return [
    {
      consentType: 'concierge_act_on_behalf',
      version: CONCIERGE_CONSENT_VERSION,
      status: input.actOnBehalf ? 'granted' : 'declined',
      sourceSurface: 'booking_flow_concierge',
      artifactRef,
    },
    {
      consentType: 'concierge_share_with_provider',
      version: CONCIERGE_CONSENT_VERSION,
      status: input.shareWithProvider ? 'granted' : 'declined',
      sourceSurface: 'booking_flow_concierge',
      artifactRef,
    },
    {
      consentType: 'concierge_insurance_access',
      version: CONCIERGE_CONSENT_VERSION,
      status: input.accessInsuranceInfo ? 'granted' : 'declined',
      sourceSurface: 'booking_flow_concierge',
      artifactRef,
    },
  ];
}

export function buildConsentSnapshotMap(events: Array<{ consentType: ConsentType; status: 'granted' | 'declined' | 'revoked' }>) {
  return events.reduce<Record<string, 'granted' | 'declined' | 'revoked'>>((accumulator, event) => {
    accumulator[event.consentType] = event.status;
    return accumulator;
  }, {});
}

/* ─────────────────── First-login consent gate ─────────────────── */

/**
 * Read the member's current consent summaries.
 *
 * `users/{uid}/consents/{consentType}` is written server-side only (the client
 * is denied writes in firestore.rules), but the owner may read it — which is
 * what lets the gate decide whether it needs to appear at all.
 */
export async function fetchConsentSummaries(
  userId: string,
): Promise<Record<string, { status: ConsentStatus; version: string }>> {
  const snap = await getDocs(collection(db, 'users', userId, 'consents'));
  const out: Record<string, { status: ConsentStatus; version: string }> = {};
  snap.forEach((docSnap) => {
    const data = docSnap.data() as { latestStatus?: ConsentStatus; latestVersion?: string };
    if (!data?.latestStatus) return;
    out[docSnap.id] = {
      status: data.latestStatus,
      version: data.latestVersion || '',
    };
  });
  return out;
}

/**
 * True when the member still owes us consent — either they have never granted
 * one of the required types, or they granted it against an older version of the
 * documents and so have not agreed to the current text.
 *
 * Optional consents are deliberately NOT considered: refusing them must never
 * block access, or they would not be freely given.
 */
export function needsConsent(
  summaries: Record<string, { status: ConsentStatus; version: string }>,
  currentVersion: string = ONBOARDING_CONSENT_VERSION,
): boolean {
  return REQUIRED_CONSENT_TYPES.some((type) => {
    const entry = summaries[type];
    return !entry || entry.status !== 'granted' || entry.version !== currentVersion;
  });
}

/**
 * Build the events written when the gate is submitted.
 *
 * REQUIRED types only. The gate deliberately does not present the optional
 * consent (genetic_data_retention), so it must not record a status for it either
 * — writing 'declined' for a question the member was never asked would
 * misrepresent a refusal they never made. It is collected at enrolment, where it
 * is actually put to them, and changeable afterwards from the consent screen.
 */
export function buildFirstLoginConsentEvents(): ConsentEventInput[] {
  return REQUIRED_CONSENT_TYPES.map((consentType) => ({
    consentType,
    version: ONBOARDING_CONSENT_VERSION,
    status: 'granted' as ConsentStatus,
    sourceSurface: 'member_app_first_login' as const,
  }));
}
